import React, {useEffect, useMemo, useState, useRef} from 'react';
import {FloatingToolbar} from '@techsee/techsee-ui-common/lib/floating-toolbar';
import {Boundaries} from '@techsee/techsee-ui-common/lib/draggble-panel/contracts';
import {DeviceCategory} from '@techsee/techsee-common/lib/constants/utils.constant';
import {IFloatingToolbarGroup} from '@techsee/techsee-ui-common/lib/floating-toolbar/contracts';
import {RECORDING_STATUS} from '@techsee/techsee-common/lib/constants/room.constants';
import '../../../../../components/ts-floating-toolbar/ts-floating-toolbar.style.scss';

export type ToolbarHost = {
    isToggleEnabled_SessionToolbar: () => boolean;
    customerEndedTheMeeting: boolean;
    isRecordingEnabled: boolean;
    recordButtonReady: boolean;
    recordingStatusMessage: () => string;
    recordingStatus: RECORDING_STATUS;
    onRecordButton: () => void;
    on?: (eventName: string | symbol, listener: (...args: any[]) => void) => any;
    off?: (eventName: string | symbol, listener: (...args: any[]) => void) => any;
};

type Position = {x: number; y: number};

const TOOLBAR_MARGIN = 30;

const findMainContentElement = (): HTMLElement | null => {
    return (
        (document.querySelector('.live-dashboard-main-content') as HTMLElement) ||
        (document.querySelector('.mini-dashboard-main-content') as HTMLElement)
    );
};

type Props = {controller: ToolbarHost};

export const NwayFloatingToolbar: React.FC<Props> = ({controller}) => {
    // Position state
    const [position, setPosition] = useState<Position>({x: -999, y: -999});
    const [initialized, setInitialized] = useState<boolean>(false);

    // Recording state with safe defaults
    const [recordingStatus, setRecordingStatus] = useState<RECORDING_STATUS>(RECORDING_STATUS.ready);
    const [recordingMessage, setRecordingMessage] = useState<string>('');
    const [isRecordingEnabled, setIsRecordingEnabled] = useState<boolean>(false);
    const [recordButtonReady, setRecordButtonReady] = useState<boolean>(false);

    const boundaries: Boundaries = useMemo(
        () => ({left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight}),
        []
    );
    const offset: Boundaries = useMemo(() => ({left: 0, top: 0, right: 0, bottom: 0}), []);

    useEffect(() => {
        if (!controller || !controller.on) return;

        const handleRecordingStatusChanged = (status: RECORDING_STATUS) => {
            if (status) {
                setRecordingStatus(status);

                if (controller.recordingStatusMessage) {
                    try {
                        const message = controller.recordingStatusMessage();
                        setRecordingMessage(message);
                    } catch (e) {
                        console.error('Error updating recording message:', e);
                    }
                }
            }
        };

        controller.on('recordingStatusChanged', handleRecordingStatusChanged);

        return () => {
            if (controller.off) {
                controller.off('recordingStatusChanged', handleRecordingStatusChanged);
            }
        };
    }, [controller]);

    useEffect(() => {
        if (!controller) return;

        try {
            const status = controller.recordingStatus || RECORDING_STATUS.ready;
            let message = '';

            if (controller.recordingStatusMessage && typeof controller.recordingStatusMessage === 'function') {
                try {
                    message = controller.recordingStatusMessage();
                } catch (e) {
                    console.error('Error calling recordingStatusMessage:', e);
                }
            }

            const enabled = !!controller.isRecordingEnabled;
            const ready = !!controller.recordButtonReady;

            setRecordingStatus(status);
            setRecordingMessage(message);
            setIsRecordingEnabled(enabled);
            setRecordButtonReady(ready);
        } catch (error) {
            console.error('Error updating recording status:', error);
        }
    }, [controller, controller?.recordingStatus, controller?.recordButtonReady, controller?.isRecordingEnabled]);

    const options: IFloatingToolbarGroup[] = useMemo(() => {
        return [
            {
                groupId: 'Group-recording',
                available: isRecordingEnabled,
                items: [
                    {
                        title: recordingMessage,
                        icon: 'recodring',
                        available: isRecordingEnabled,
                        disabled: !recordButtonReady,
                        toggleOnClick: true,
                        activatable: true,
                        iconClassName: `icon-recording-${recordingStatus}`,
                        clickHandler: () => {
                            try {
                                if (controller && controller.onRecordButton) {
                                    const result = controller.onRecordButton();

                                    // Force immediate UI update
                                    if (controller.recordingStatus) {
                                        setRecordingStatus(controller.recordingStatus);

                                        // Update message too if possible
                                        if (controller.recordingStatusMessage) {
                                            try {
                                                const updatedMessage = controller.recordingStatusMessage();
                                                setRecordingMessage(updatedMessage);
                                            } catch (e) {
                                                console.error('Error getting updated message:', e);
                                            }
                                        }
                                    }

                                    return result;
                                }
                            } catch (error) {
                                console.error('Error handling record button click:', error);
                            }
                        }
                    }
                ]
            }
        ];
    }, [isRecordingEnabled, recordButtonReady, recordingStatus, recordingMessage, controller]);

    useEffect(() => {
        const recalc = () => {
            const el = findMainContentElement();
            if (!el) return;

            const rect = el.getBoundingClientRect();
            const toolbarEl = document.querySelector('.floating-toolbar') as HTMLElement | null;
            const tbWidth = toolbarEl?.offsetWidth || 250;
            const tbHeight = toolbarEl?.offsetHeight || 80;
            const toolbarX = rect.right - TOOLBAR_MARGIN - tbWidth;
            const toolbarY = rect.bottom - TOOLBAR_MARGIN - tbHeight;

            setPosition({x: toolbarX, y: toolbarY});
            setInitialized(true);
        };

        const id = window.setTimeout(recalc, 0);
        window.addEventListener('resize', recalc);

        return () => {
            window.removeEventListener('resize', recalc);
            window.clearTimeout(id);
        };
    }, []);

    if (!initialized) {
        return null;
    }

    // Add safety checks for controller
    try {
        if (
            !controller ||
            !controller.isToggleEnabled_SessionToolbar ||
            typeof controller.isToggleEnabled_SessionToolbar !== 'function' ||
            !controller.isToggleEnabled_SessionToolbar() ||
            controller.customerEndedTheMeeting
        ) {
            return null;
        }
    } catch (error) {
        console.error('Error checking toolbar visibility conditions:', error);
        return null;
    }

    return (
        <div className='use-common-styles floating-toolbar-style'>
            <FloatingToolbar
                position={position}
                boundaries={boundaries}
                offset={offset}
                options={options}
                deviceCategory={DeviceCategory.desktop}
            />
        </div>
    );
};

export default NwayFloatingToolbar;
